import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUser, FaEnvelope, FaPhone, FaSearch, FaDownload, FaSync } from 'react-icons/fa';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

const StudentTestRecords = () => {
  const [testRecords, setTestRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [enrichedRecords, setEnrichedRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTests, setExpandedTests] = useState({});
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);

  // Define test types tabs
  const testTypes = [
    { id: "ALL", label: "ALL TESTS" },
    { id: "TEST_SERIES", label: "TEST SERIES" },
    { id: "COURSE_TEST", label: "RECORDED COURSE TESTS" },
    { id: "SCHOLARSHIP_TEST", label: "SCHOLARSHIP TESTS" },
    { id: "TEST_OF_THE_DAY", label: "TEST OF THE DAY" },
    { id: "WEEKLY_TEST", label: "LIVE CLASSES TEST" }
  ];

  useEffect(() => {
    // Log filtered records when they change
    console.log("Filtered records updated:", filteredRecords);
  }, [filteredRecords]);

  useEffect(() => {
    fetchTestRecords();
  }, []);

  // Fetch test records with student details
  const fetchTestRecords = async () => {
    try {
      setLoading(true);
      
      console.log("Fetching test records...");
      
      // Fetch all test records directly from the API
      const testResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/student-test-records');
      const records = testResponse.data.data || [];
      
      // Fetch all unique student IDs
      const uniqueStudentIds = [...new Set(records.map(record => record.s_id))];
      
      // Fetch student details for each unique student ID
      const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
        try {
          const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`);
          return { studentId, details: response.data };
        } catch (err) {
          console.error(`Error fetching details for student ${studentId}:`, err);
          return { studentId, details: null };
        }
      });
      
      const studentDetails = await Promise.all(studentDetailsPromises);
      
      // Create a map of student details
      const studentDetailsMap = studentDetails.reduce((acc, { studentId, details }) => {
        if (details) {
          acc[studentId] = details;
        }
        return acc;
      }, {});
      
      // Enrich records with student details
      const enrichedRecords = records.map(record => ({
        ...record,
        student_name: studentDetailsMap[record.s_id]?.f_name || 'Unknown Student',
        phone_number: studentDetailsMap[record.s_id]?.phonenumber || `ID: ${record.s_id}`,
      }));
      
      console.log("Enriched test records:", enrichedRecords);
      
      setTestRecords(records);
      setFilteredRecords(enrichedRecords);
      setEnrichedRecords(enrichedRecords);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tests by type with student details
  const fetchTestsByType = async (type) => {
    try {
      setLoading(true);
      setActiveTab(type);
      
      if (type === "ALL") {
        // Get all records
        const response = await axios.get('https://apissc.sainikschoolcadet.com/api/student-test-records');
        const records = response.data.data || [];
        
        // Fetch all unique student IDs
        const uniqueStudentIds = [...new Set(records.map(record => record.s_id))];
        
        // Fetch student details for each unique student ID
        const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
          try {
            const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`);
            return { studentId, details: response.data };
          } catch (err) {
            console.error(`Error fetching details for student ${studentId}:`, err);
            return { studentId, details: null };
          }
        });
        
        const studentDetails = await Promise.all(studentDetailsPromises);
        
        // Create a map of student details
        const studentDetailsMap = studentDetails.reduce((acc, { studentId, details }) => {
          if (details) {
            acc[studentId] = details;
          }
          return acc;
        }, {});
        
        // Enrich records with student details
        const enrichedRecords = records.map(record => ({
          ...record,
          student_name: studentDetailsMap[record.s_id]?.f_name || 'Unknown Student',
          phone_number: studentDetailsMap[record.s_id]?.phonenumber || `ID: ${record.s_id}`,
        }));
        
        setTestRecords(records);
        setFilteredRecords(enrichedRecords);
        setEnrichedRecords(enrichedRecords);
      } else {
        // Get records by type
        try {
          const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/student-test-records/type/${type}`);
          const records = response.data.data || [];
          
          // Fetch all unique student IDs
          const uniqueStudentIds = [...new Set(records.map(record => record.s_id))];
          
          // Fetch student details for each unique student ID
          const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
            try {
              const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`);
              return { studentId, details: response.data };
            } catch (err) {
              console.error(`Error fetching details for student ${studentId}:`, err);
              return { studentId, details: null };
            }
          });
          
          const studentDetails = await Promise.all(studentDetailsPromises);
          
          // Create a map of student details
          const studentDetailsMap = studentDetails.reduce((acc, { studentId, details }) => {
            if (details) {
              acc[studentId] = details;
            }
            return acc;
          }, {});
          
          // Enrich records with student details
          const enrichedRecords = records.map(record => ({
            ...record,
            student_name: studentDetailsMap[record.s_id]?.f_name || 'Unknown Student',
            phone_number: studentDetailsMap[record.s_id]?.phonenumber || `ID: ${record.s_id}`,
          }));
          
          setTestRecords(records);
          setFilteredRecords(enrichedRecords);
          setEnrichedRecords(enrichedRecords);
        } catch (typeError) {
          // Fallback to filtering all records client-side if the type endpoint fails
          console.error('Error with type endpoint, falling back to client filtering:', typeError);
          const allResponse = await axios.get('https://apissc.sainikschoolcadet.com/api/student-test-records');
          const allRecords = allResponse.data.data || [];
          
          // Fetch all unique student IDs
          const uniqueStudentIds = [...new Set(allRecords.map(record => record.s_id))];
          
          // Fetch student details for each unique student ID
          const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
            try {
              const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${studentId}`);
              return { studentId, details: response.data };
            } catch (err) {
              console.error(`Error fetching details for student ${studentId}:`, err);
              return { studentId, details: null };
            }
          });
          
          const studentDetails = await Promise.all(studentDetailsPromises);
          
          // Create a map of student details
          const studentDetailsMap = studentDetails.reduce((acc, { studentId, details }) => {
            if (details) {
              acc[studentId] = details;
            }
            return acc;
          }, {});
          
          const filtered = allRecords.filter(record => 
            record.test_type.replace(/\s/g, "_").toUpperCase() === type
          );
          
          // Enrich filtered records with student details
          const enrichedRecords = filtered.map(record => ({
            ...record,
            student_name: studentDetailsMap[record.s_id]?.f_name || 'Unknown Student',
            phone_number: studentDetailsMap[record.s_id]?.phonenumber || `ID: ${record.s_id}`,
          }));
          
          setTestRecords(allRecords);
          setFilteredRecords(enrichedRecords);
          setEnrichedRecords(enrichedRecords);
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data by type:', err);
    } finally {
      setLoading(false);
    }
  };

 

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);

    // Filter records based on search term
    const filtered = enrichedRecords.filter(record => 
      record.student_name.toLowerCase().includes(searchValue) ||
      record.s_id.toString().includes(searchValue) ||
      record.test_title.toLowerCase().includes(searchValue)
    );

    setFilteredRecords(filtered);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Handle export of records
  

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Test Records</h1>
        <p className="text-gray-600">View and manage student test attempts</p>
      </div>
      
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {testTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => fetchTestsByType(type.id)}
                className={`py-3 px-4 font-medium ${
                  activeTab === type.id 
                    ? "text-blue-600 border-b-2 border-blue-600" 
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg shadow-sm">
        {/* Search and Export Row */}
        <div className="p-4 border-b">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="w-full md:w-1/3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by test title, student name, or ID..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={fetchTestRecords}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-md flex items-center gap-2"
              >
                <FaSync className="text-gray-600" />
                Refresh
              </button>
              

            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Loading test records...</p>
          </div>
        )}
        {/* Error State */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center h-64 p-6">
            <div className="text-red-500 bg-red-100 p-3 rounded-full mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-800 mb-2">Request Failed</h3>
            <p className="text-gray-600 mb-4">Error: {error}</p>
            <button 
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchTestsByType(activeTab);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center gap-2"
            >
              <FaSync className="text-white" />
              Retry
            </button>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && filteredRecords.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 p-6">
            <div className="bg-blue-50 p-4 rounded-full mb-4">
              <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-800 mb-2">No Test Records Found</h3>
            <p className="text-gray-600 mb-6">There are no test records available for the selected criteria.</p>
            <button 
              onClick={() => {
                setActiveTab("ALL");
                setSearchTerm("");
                fetchTestsByType("ALL");
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center gap-2"
            >
              <FaSync className="text-white" />
              Reset Filters
            </button>
          </div>
        )}
        
        {/* Test Records Table */}
        {!loading && !error && filteredRecords.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((test, index) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          {test.student_name && test.student_name !== "Unknown Student" 
                            ? test.student_name.charAt(0).toUpperCase() 
                            : 'T'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {test.student_name || 'Unknown Student'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {test.phone_number && !test.phone_number.startsWith('ID:') 
                              ? test.phone_number 
                              : `ID: ${test.s_id}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{test.test_title}</div>
                      <div className="text-sm text-gray-500">ID: {test.test_id || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          test.test_type.includes("SCHOLARSHIP") ? "bg-green-100 text-green-800" :
                          test.test_type.includes("WEEKLY") ? "bg-purple-100 text-purple-800" :
                          test.test_type.includes("SERIES") ? "bg-blue-100 text-blue-800" :
                          test.test_type.includes("DAY") ? "bg-yellow-100 text-yellow-800" :
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {test.test_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center">
                          <span className={`font-medium ${
                            (test.marks_scored / test.total_marks) >= 0.7 ? "text-green-600" :
                            (test.marks_scored / test.total_marks) >= 0.4 ? "text-yellow-600" :
                            "text-red-600"
                          }`}>
                            {test.marks_scored}/{test.total_marks} ({Math.round((test.marks_scored / test.total_marks) * 100)}%)
                          </span>
                        </div>
                        <div className="w-28 bg-gray-200 rounded-full h-1.5 mt-1">
                          <div 
                            className={`h-1.5 rounded-full ${
                              (test.marks_scored / test.total_marks) >= 0.7 ? "bg-green-600" :
                              (test.marks_scored / test.total_marks) >= 0.4 ? "bg-yellow-600" :
                              "bg-red-600"
                            }`}
                            style={{width: `${Math.round((test.marks_scored / test.total_marks) * 100)}%`}}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(test.attempted_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        
                        <button
                          onClick={() => window.open(`/view-student/${test.s_id}`, '_blank')}
                          className="text-green-500 hover:text-green-700 p-2 rounded-full hover:bg-green-50"
                          title="View Student Profile"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        
        
        {/* Pagination */}
        {!loading && !error && filteredRecords.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                Previous
              </button>
              <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredRecords.length}</span> of{' '}
                  <span className="font-medium">{filteredRecords.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                    <span className="sr-only">Previous</span>
                    {'<'}
                  </button>
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                    1
                  </button>
                  <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                    <span className="sr-only">Next</span>
                    {'>'}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentTestRecords;